import { useQuery } from '@apollo/client';
import type { DragEndEvent } from '@dnd-kit/core';
import { DndContext, DragOverlay } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Apps, Lock, RemoveCircle } from '@mui/icons-material';
import { Box, Button, List, ListItem, ListItemText, Skeleton } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconButton } from '~/components/IconButton';
import { ItemThumbnail } from '~/components/ItemThumbnail';
import { DragHandle } from '~/components/list-table';
import { DragGhost } from '~/components/list-table/DragGhost';
import { useAppContext } from '~/contexts';
import {
  sortableCollisionDetection,
  sortableDraggingSx,
  useSortableSensors,
} from '~/hooks/sortable';
import { AppSelectAppsDocument, type AppSelectApps__App as App } from './AppSelectField.generated';
import { MediaMultiSelectDialog } from './MediaMultiSelectField';

interface AppRowProps {
  /** Missing when the user can't see the app (or it was deleted); the row still renders. */
  app: App | undefined;
  disabled?: boolean;
  id: number;
  loading: boolean;
  onRemove: () => void;
  sortable: boolean;
}

const AppRow = ({ app, disabled, id, loading, onRemove, sortable }: AppRowProps) => {
  const { t } = useTranslation();
  const {
    attributes,
    isDragging,
    listeners,
    setActivatorNodeRef,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id, disabled: disabled || !sortable });

  const name = app?.name ?? t('components.app_select.restricted');

  return (
    <ListItem
      ref={setNodeRef}
      divider
      secondaryAction={
        <IconButton
          disabled={disabled}
          label={t('components.app_select.remove', { name })}
          onClick={onRemove}
          size="small"
        >
          <RemoveCircle color="error" fontSize="small" />
        </IconButton>
      }
      sx={{
        gap: 1,
        opacity: isDragging ? 0 : 1,
        pl: 0,
        transform: CSS.Transform.toString(transform),
        transition,
      }}
    >
      <Box sx={{ alignItems: 'center', display: 'flex', flexShrink: 0, gap: 1 }}>
        {sortable && (
          <DragHandle
            {...attributes}
            {...listeners}
            dragDisabled={disabled}
            isDragging={isDragging}
            ref={setActivatorNodeRef}
          />
        )}
        <ItemThumbnail
          fallbackIcon={
            app || loading ? (
              <Apps sx={{ color: 'text.secondary', fontSize: 20 }} />
            ) : (
              <Lock sx={{ color: 'text.secondary', fontSize: 20 }} />
            )
          }
          thumbnailUri={app?.thumbnailUri}
        />
      </Box>
      <ListItemText
        primary={loading && !app ? <Skeleton width="60%" /> : name}
        primaryTypographyProps={{ noWrap: true, title: name }}
        secondary={!loading && !app ? t('components.app_select.restricted_description') : undefined}
        sx={{ minWidth: 0 }}
      />
    </ListItem>
  );
};

export interface AppSelectProps {
  /** The app being edited, kept out of the picker so it can't list itself. */
  contentItemId?: number;
  disabled?: boolean;
  label: string;
  /** Lists several apps in the order picked; otherwise holds one. */
  multiple: boolean;
  onChange: (ids: number[]) => void;
  value: readonly number[];
}

export const AppSelect = ({
  contentItemId,
  disabled,
  label,
  multiple,
  onChange,
  value,
}: AppSelectProps) => {
  const { t } = useTranslation();
  const { currentNetwork } = useAppContext();
  const sensors = useSortableSensors();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeId, setActiveId] = useState<number | null>(null);

  // Sorted, so reordering doesn't change the variables and refetch
  const ids = [...value].sort((a, b) => a - b);
  const { data, loading, previousData } = useQuery(AppSelectAppsDocument, {
    variables: { ids, networkId: currentNetwork.id },
    skip: ids.length === 0,
  });
  const appsById = new Map(
    (data ?? previousData)?.network?.contentItems.map((app) => [app.id, app]),
  );

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    setActiveId(null);
    if (!over || active.id === over.id) return;
    onChange(arrayMove([...value], value.indexOf(+active.id), value.indexOf(+over.id)));
  };

  const title = multiple
    ? t('components.app_select.select_apps')
    : t('components.app_select.select_app');

  return (
    <Box sx={{ alignItems: 'flex-end', display: 'flex', flexDirection: 'column', gap: 1 }}>
      {value.length > 0 && (
        <DndContext
          collisionDetection={sortableCollisionDetection}
          modifiers={[restrictToVerticalAxis]}
          onDragCancel={() => setActiveId(null)}
          onDragEnd={handleDragEnd}
          onDragStart={({ active }) => setActiveId(+active.id)}
          sensors={sensors}
        >
          <SortableContext items={[...value]} strategy={verticalListSortingStrategy}>
            <List
              aria-label={label}
              data-dragging={activeId == null ? undefined : 'true'}
              disablePadding
              sx={{ width: '100%', ...sortableDraggingSx }}
            >
              {value.map((id) => (
                <AppRow
                  app={appsById.get(id)}
                  disabled={disabled}
                  id={id}
                  key={id}
                  loading={loading}
                  onRemove={() => onChange(value.filter((x) => x !== id))}
                  sortable={multiple}
                />
              ))}
            </List>
          </SortableContext>
          <DragOverlay dropAnimation={null}>
            {activeId != null && (
              <DragGhost
                name={appsById.get(activeId)?.name ?? t('components.app_select.restricted')}
              />
            )}
          </DragOverlay>
        </DndContext>
      )}

      <Button
        color="primary"
        disabled={disabled}
        onClick={() => setDialogOpen(true)}
        size="small"
        startIcon={<Apps />}
        variant="contained"
      >
        {title}
      </Button>

      {dialogOpen && (
        <MediaMultiSelectDialog
          description={t('components.app_select.dialog_description')}
          excludeContentItemIds={contentItemId == null ? undefined : [contentItemId]}
          hideUpload
          kind={['APP']}
          onChange={(selectedIds) => onChange(selectedIds.map((x) => Number(x.split('-').at(1))))}
          onClose={() => setDialogOpen(false)}
          open
          single={!multiple}
          title={title}
          value={value.map((id) => `ContentItem-${id}`)}
        />
      )}
    </Box>
  );
};
